/**
 * Owns the voice WebSocket connection, mic capture, and TTS playback for
 * VoiceMode. Protocol (see Broker/voice_ws.py):
 *
 * client -> server: {"type":"auth","token","session_id"} first, then binary
 *   PCM16/16kHz mic chunks, optionally {"type":"reauth","token"}.
 * server -> client: {"type":"partial_transcript","text"},
 *   {"type":"final_transcript","text"} (committed user turn),
 *   {"type":"assistant_partial","text"} (reply transcript streamed in step
 *   with the spoken audio, for the live caption),
 *   {"type":"assistant_text","text"} (the finished reply, live caption),
 *   {"type":"turn_committed","user_text","assistant_text","grounded"} —
 *     the turn as actually stored, and the ONLY thing chat history is built
 *     from; {"type":"turn_discarded","reason"} — the server dropped the turn
 *     (echo) and it must not appear in the chat,
 *   {"type":"result_card",...}, {"type":"tts_start"}, binary PCM16/24kHz
 *   audio chunks, {"type":"tts_cancelled"}, {"type":"tts_end"}.
 *
 * The transcript events are captions in flight and can be superseded; only
 * turn_committed is authoritative. Appending chat turns from the captions is
 * what let the on-screen conversation drift from the stored one.
 *
 * getAccessToken() (not useAuth()) is the token source, matching the
 * existing raw-fetch pattern in ChatContainer.js — AuthContext doesn't
 * expose the raw token itself.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { WS_BACKEND_URL } from '../environment';
import { getAccessToken } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { PlaybackQueue } from '../audio/playback-queue';

const MIC_SAMPLE_TARGET = 16000;
// Access tokens are 30 min (CLAUDE.md); refresh a minute early so a long
// voice session doesn't get cut off mid-conversation.
const TOKEN_LIFETIME_MS = 30 * 60 * 1000;
const REAUTH_MARGIN_MS = 60 * 1000;
// If mic permission, the WS handshake, or the audio pipeline setup hasn't
// reached 'listening' within this window, force-abort into 'error' rather
// than leaving the UI stuck on "Connecting" forever with no way out except
// a page reload — a real gap this hit in production (a server-side restart
// killed an in-flight connection with no clean close reaching the client).
const CONNECT_TIMEOUT_MS = 15000;

const CLOSE_AUTH_EXPIRED = 4419;
const CLOSE_NORMAL = 1000;

export function useVoiceSession({ sessionId, onUserTranscript, onAssistantReply, onResultCard, onTurnCommitted, onTurnDiscarded }) {
  const { silentRefresh } = useAuth();
  const [voiceState, setVoiceState] = useState('idle'); // idle|connecting|listening|thinking|speaking|error
  const [partialTranscript, setPartialTranscript] = useState('');
  const [resultCards, setResultCards] = useState([]);
  const [assistantPartial, setAssistantPartial] = useState('');

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const workletRef = useRef(null);
  const micStreamRef = useRef(null);
  const playbackRef = useRef(null);
  const reauthTimerRef = useRef(null);
  const startVoiceRef = useRef(null);
  const connectTimerRef = useRef(null);
  const awaitingFirstAudioRef = useRef(false);
  const echoGateRef = useRef(null);
  const lastGateStateRef = useRef(false);

  const cleanupAudio = useCallback(() => {
    if (echoGateRef.current) {
      clearInterval(echoGateRef.current);
      echoGateRef.current = null;
    }
    lastGateStateRef.current = false;
    if (workletRef.current) {
      try { workletRef.current.disconnect(); } catch (_) { /* already gone */ }
      workletRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) { /* already closed */ }
      audioCtxRef.current = null;
    }
    if (playbackRef.current) {
      playbackRef.current.close();
      playbackRef.current = null;
    }
  }, []);

  const stopVoice = useCallback(() => {
    if (reauthTimerRef.current) {
      clearTimeout(reauthTimerRef.current);
      reauthTimerRef.current = null;
    }
    if (connectTimerRef.current) {
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
    }
    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      ws.close(CLOSE_NORMAL);
    }
    cleanupAudio();
    setVoiceState('idle');
    setPartialTranscript('');
    setResultCards([]);
  }, [cleanupAudio]);

  const scheduleReauth = useCallback(() => {
    if (reauthTimerRef.current) clearTimeout(reauthTimerRef.current);
    const delay = Math.max(TOKEN_LIFETIME_MS - REAUTH_MARGIN_MS, 5000);
    reauthTimerRef.current = setTimeout(async () => {
      const ok = await silentRefresh();
      if (ok && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'reauth', token: getAccessToken() }));
        scheduleReauth();
      }
    }, delay);
  }, [silentRefresh]);

  const startVoice = useCallback(async () => {
    if (!sessionId) return;
    // Defensive, not just an optimization: if a previous attempt's
    // WebSocket died without a clean close event reaching the browser
    // (exactly what happens if the server restarts mid-connection),
    // wsRef.current can be left pointing at a zombie socket. Silently
    // bailing out here — the old behavior — meant every subsequent retry
    // did nothing at all: no getUserMedia call, no new WebSocket, no
    // visible error, just stuck. Tear down anything stale and always
    // proceed with a fresh attempt instead.
    if (wsRef.current) {
      const stale = wsRef.current;
      wsRef.current = null;
      try { stale.close(); } catch (_) { /* already dead */ }
    }
    if (connectTimerRef.current) {
      clearTimeout(connectTimerRef.current);
    }
    cleanupAudio();

    setVoiceState('connecting');
    setResultCards([]);

    connectTimerRef.current = setTimeout(() => {
      console.error('voice: connect timed out after', CONNECT_TIMEOUT_MS, 'ms');
      if (wsRef.current) {
        const timedOut = wsRef.current;
        wsRef.current = null;
        try { timedOut.close(); } catch (_) { /* ignore */ }
      }
      cleanupAudio();
      setVoiceState('error');
    }, CONNECT_TIMEOUT_MS);

    let micStream;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
    } catch (err) {
      console.error('voice: mic permission denied or unavailable', err);
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
      setVoiceState('error');
      return;
    }
    micStreamRef.current = micStream;

    const ws = new WebSocket(`${WS_BACKEND_URL}/ws/voice`);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = async () => {
      ws.send(JSON.stringify({ type: 'auth', token: getAccessToken(), session_id: sessionId }));
      scheduleReauth();

      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioCtx();
        audioCtxRef.current = audioCtx;
        await audioCtx.audioWorklet.addModule(new URL('../audio/pcm-worklet.js', import.meta.url));

        const source = audioCtx.createMediaStreamSource(micStream);
        const worklet = new AudioWorkletNode(audioCtx, 'pcm-worklet-processor', {
          processorOptions: { targetSampleRate: MIC_SAMPLE_TARGET },
        });
        worklet.port.onmessage = (e) => {
          if (ws.readyState === WebSocket.OPEN) ws.send(e.data);
        };
        source.connect(worklet);
        workletRef.current = worklet;

        playbackRef.current = new PlaybackQueue();
        // Keep the mic gate in step with what is actually coming out of the
        // speakers. Driven by the playback queue rather than tts_start/tts_end
        // because the client keeps playing for seconds after the server has
        // finished sending — gating on the messages would unmute the mic
        // while the assistant is still audible, which is exactly when the
        // echo gets in.
        echoGateRef.current = setInterval(() => {
          const speaking = !!(playbackRef.current && playbackRef.current.isPlaying());
          if (speaking !== lastGateStateRef.current && workletRef.current) {
            lastGateStateRef.current = speaking;
            workletRef.current.port.postMessage({ type: 'assistantSpeaking', value: speaking });
          }
        }, 50);
        if (connectTimerRef.current) {
          clearTimeout(connectTimerRef.current);
          connectTimerRef.current = null;
        }
        setVoiceState('listening');
      } catch (err) {
        console.error('voice: failed to start mic capture pipeline', err);
        if (connectTimerRef.current) {
          clearTimeout(connectTimerRef.current);
          connectTimerRef.current = null;
        }
        setVoiceState('error');
        ws.close();
      }
    };

    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (_) {
          return;
        }
        switch (data.type) {
          case 'partial_transcript':
            // The server sends each transcription fragment as a delta (it
            // does `user_text += ev.text` itself), so replacing here showed
            // only the last fragment of a sentence. Accumulate, exactly as
            // assistant_partial below already does.
            setPartialTranscript((prev) => prev + data.text);
            break;
          case 'final_transcript':
            setPartialTranscript('');
            setAssistantPartial('');
            // The result card is deliberately NOT cleared here. A
            // recommendation used to vanish the moment the user spoke again,
            // so it was only readable while the reply was still playing —
            // too short to actually read. It now stays until a newer card
            // replaces it, or the session ends.
            setVoiceState('thinking');
            if (onUserTranscript) onUserTranscript(data.text);
            break;
          case 'assistant_partial':
            // Streamed in step with the spoken audio, so the caption tracks
            // the voice instead of appearing after it.
            setAssistantPartial((prev) => prev + data.text);
            break;
          case 'assistant_text':
            if (onAssistantReply) onAssistantReply(data.text);
            break;
          case 'turn_committed':
            // Authoritative: this pair is in the database. Chat history is
            // appended from here and nowhere else, so what stays on screen
            // after the call is what a reload returns.
            setPartialTranscript('');
            if (onTurnCommitted) onTurnCommitted(data);
            break;
          case 'turn_discarded':
            // The server rejected the turn (the assistant hearing itself).
            // Drop the captions so it cannot survive as a chat turn.
            setPartialTranscript('');
            setAssistantPartial('');
            if (onTurnDiscarded) onTurnDiscarded(data);
            break;
          case 'result_card':
            // `cards` is the full result set for the carousel; the top-level
            // fields are the leading card and stay for older clients.
            setResultCards(Array.isArray(data.cards) && data.cards.length > 0 ? data.cards : [data]);
            if (onResultCard) onResultCard(data);
            break;
          case 'tts_start':
            setAssistantPartial('');
            // Don't switch to 'speaking' yet — synthesize() has a real
            // multi-second latency floor before any audio actually arrives
            // (measured ~2.5-4s baseline per TTS call). Flipping the orb to
            // "speaking" here made it animate as if talking while still
            // silent. Stay in 'thinking' until the first real audio chunk
            // lands below.
            awaitingFirstAudioRef.current = true;
            break;
          case 'tts_cancelled':
            awaitingFirstAudioRef.current = false;
            if (playbackRef.current) playbackRef.current.clear();
            setVoiceState('listening');
            break;
          case 'tts_end':
            awaitingFirstAudioRef.current = false;
            setVoiceState('listening');
            break;
          case 'ping':
            // Heartbeat from the server (voice_ws.py) — reply so it knows
            // the client is still alive. Not actually load-bearing for the
            // server's own idle-timeout (mic audio chunks already count as
            // activity), but keeps the exchange symmetric.
            if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'pong' }));
            break;
          default:
            break;
        }
      } else if (playbackRef.current) {
        if (awaitingFirstAudioRef.current) {
          awaitingFirstAudioRef.current = false;
          setVoiceState('speaking');
        }
        playbackRef.current.enqueue(event.data);
      }
    };

    ws.onclose = (event) => {
      const wasCurrent = wsRef.current === ws;
      wsRef.current = null;
      cleanupAudio();
      if (connectTimerRef.current) {
        clearTimeout(connectTimerRef.current);
        connectTimerRef.current = null;
      }

      if (event.code === CLOSE_AUTH_EXPIRED && wasCurrent) {
        silentRefresh().then((ok) => {
          if (ok) startVoiceRef.current();
          else setVoiceState('error');
        });
        return;
      }
      setVoiceState(event.code === CLOSE_NORMAL ? 'idle' : 'error');
    };

    ws.onerror = () => {
      setVoiceState('error');
    };
  }, [sessionId, onUserTranscript, onAssistantReply, onResultCard, onTurnCommitted, onTurnDiscarded, cleanupAudio, scheduleReauth, silentRefresh]);

  // Lets the onclose handler above call the latest startVoice without
  // taking a stale closure over it.
  useEffect(() => {
    startVoiceRef.current = startVoice;
  }, [startVoice]);

  useEffect(() => stopVoice, [stopVoice]);

  return { voiceState, partialTranscript, assistantPartial, resultCards, startVoice, stopVoice };
}
